import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { IMG_HERO, IMG_FERN } from "@/lib/images";
import WeatherWidget from "@/components/WeatherWidget";
import { GalleryCard, ArticleCard } from "@/components/Cards";
import { HomeGreeting } from "@/components/DateTime";

const PRINCIPLES = [
  ["01", "Non-sexual", "Naturism, not display. Every image is tasteful and consented."],
  ["02", "Public by default", "No hidden sections, no paywalls. Read freely."],
  ["03", "Grounded locally", "Real places, real conditions, written from Aotearoa."],
];

function Hero() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
      <div className="lg:col-span-5 lg:pb-12">
        <HomeGreeting />
        <p className="eyebrow rise">Naturism · Aotearoa New Zealand</p>
        <h1 className="rise d1 mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1a261f]" data-testid="hero-title">
          Honest, open naturism <em className="text-[#1f402b]">from Aotearoa.</em>
        </h1>
        <p className="rise d2 mt-6 text-base sm:text-lg text-[#5a665e] leading-relaxed max-w-md">
          Places, people and practical guidance — presented with care, for adults who value sun, sea and the freedom of fewer layers.
        </p>
        <div className="rise d3 mt-9 flex flex-wrap gap-3">
          <Link to="/galleries" data-testid="hero-galleries-cta" className="bg-[#1f402b] text-white px-7 py-3.5 text-sm tracking-wide hover:bg-[#2c5a3d] transition-colors duration-200 inline-flex items-center gap-2">
            View galleries <ArrowRight size={15} />
          </Link>
          <Link to="/articles" data-testid="hero-articles-cta" className="border border-[#1f402b]/30 px-7 py-3.5 text-sm text-[#1f402b] hover:border-[#1f402b] transition-colors duration-200">
            Read the guides
          </Link>
        </div>
      </div>
      <div className="lg:col-span-7 relative rise d2">
        <div className="aspect-[4/3] sm:aspect-[16/11] overflow-hidden">
          <img src={IMG_HERO} alt="Southern lake and mountains under a clear sky" className="w-full h-full object-cover" />
        </div>
        <div className="lg:absolute lg:-left-16 lg:-bottom-10 lg:w-80 mt-4 lg:mt-0 shadow-[0_24px_60px_-30px_rgba(26,38,31,.45)]">
          <WeatherWidget />
        </div>
      </div>
    </section>
  );
}

function Principles() {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-24 sm:mt-32 grid md:grid-cols-3 border-t border-[#e2dcd4]" data-testid="principles-section">
      {PRINCIPLES.map(([n, t, d]) => (
        <div key={n} className="py-10 md:pr-10 md:border-r last:border-r-0 border-[#e2dcd4] md:[&:not(:first-child)]:pl-10">
          <p className="font-mono-k text-xs text-[#c5903b]">{n}</p>
          <h3 className="mt-3 text-2xl text-[#1f402b]">{t}</h3>
          <p className="mt-2 text-sm text-[#5a665e] leading-relaxed">{d}</p>
        </div>
      ))}
    </section>
  );
}

function SectionHead({ eyebrow, title, to, testid }) {
  return (
    <div className="flex items-end justify-between gap-6 mb-10">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight text-[#1a261f]">{title}</h2>
      </div>
      <Link to={to} className="link-u text-sm text-[#1f402b] whitespace-nowrap" data-testid={testid}>View all</Link>
    </div>
  );
}

export default function Home() {
  const [galleries, setGalleries] = useState([]);
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    api.get("/public/galleries").then(({ data }) => setGalleries(data)).catch(() => {});
    api.get("/public/articles").then(({ data }) => setArticles(data)).catch(() => {});
  }, []);
  const [lead, ...rest] = galleries;

  return (
    <div data-testid="home-page">
      <Hero />
      <Principles />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-16 sm:mt-24">
        <SectionHead eyebrow="Galleries" title="Places that feel like freedom" to="/galleries" testid="home-galleries-viewall" />
        <div className="grid md:grid-cols-12 gap-8 md:gap-10">
          {lead && <div className="md:col-span-7"><GalleryCard g={lead} tall /></div>}
          <div className="md:col-span-5 grid gap-8 md:gap-10 content-start">
            {rest.slice(0, 2).map((g) => <GalleryCard key={g.id} g={g} />)}
          </div>
        </div>
      </section>
      <section className="mt-24 sm:mt-32 bg-[#f4f0ea]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5 aspect-[4/5] overflow-hidden"><img src={IMG_FERN} alt="Silver fern detail" className="w-full h-full object-cover" /></div>
          <blockquote className="md:col-span-6 md:col-start-7">
            <p className="font-display text-3xl sm:text-4xl leading-snug text-[#1f402b] italic">“Naturism is simply being at ease — with yourself, with others, and with the land.”</p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-sm text-[#1f402b] link-u" data-testid="home-about-link">About Kiwibares <ArrowRight size={14} /></Link>
          </blockquote>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-24 sm:mt-32">
        <SectionHead eyebrow="Articles" title="Guides, etiquette & sun sense" to="/articles" testid="home-articles-viewall" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {articles.slice(0, 3).map((a) => <ArticleCard key={a.id} a={a} />)}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-24 sm:mt-32">
        <div className="border border-[#e2dcd4] bg-white p-8 sm:p-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <p className="eyebrow">Get in touch</p>
            <h2 className="mt-3 text-3xl tracking-tight text-[#1a261f]">Questions, venues, corrections?</h2>
          </div>
          <Link to="/contact" data-testid="home-contact-cta" className="bg-[#c5903b] text-white px-7 py-3.5 text-sm tracking-wide hover:bg-[#a97a2f] transition-colors duration-200 self-start md:self-auto">Send an enquiry</Link>
        </div>
      </section>
    </div>
  );
}
